import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Package, User, MapPin, Calendar, Scale, Ruler,
  Hash, FileText, CheckCircle2, ArrowUpFromLine, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` },
});

const itDate = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "-");

export default function AdminColloDetail() {
  const [params] = useSearchParams();
  const movId = params.get("mov");
  const seq = params.get("c") || "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exiting, setExiting] = useState(false);

  const load = useCallback(async () => {
    if (!movId) { setError("QR incompleto: movimento mancante"); setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/admin/warehouse/movements/${movId}/collo/${seq}`, authHeaders());
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Collo non trovato");
    } finally {
      setLoading(false);
    }
  }, [movId, seq]);

  useEffect(() => { load(); }, [load]);

  const registerExit = async () => {
    if (!window.confirm(`Registrare l'uscita del collo ${seq}?`)) return;
    setExiting(true);
    try {
      await axios.post(`${API}/admin/warehouse/qr/scan-out`,
        { payload: `MV|MOV:${movId}|C:${seq}/${data.collo.total}` }, authHeaders());
      toast.success("Uscita registrata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore registrazione uscita");
    } finally {
      setExiting(false);
    }
  };

  const m = data?.movement;
  const collo = data?.collo;
  const client = data?.client || {};

  return (
    <div className="min-h-screen bg-zinc-50 pb-16" data-testid="collo-detail-page">
      <div className="bg-[#09090B] text-white sticky top-0 z-10">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin/magazzino" className="text-white/80 hover:text-white" data-testid="collo-back">
            <ArrowLeft size={20} />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold uppercase tracking-wide">Scheda Collo</h1>
            <p className="text-[10px] text-white/60 uppercase tracking-widest">Etichetta magazzino scansionata</p>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-4 space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm py-10 justify-center">
            <Loader2 size={16} className="animate-spin" /> Caricamento collo…
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm flex items-center gap-2" data-testid="collo-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="bg-white border border-zinc-200 rounded-sm p-5 text-center">
              <div className="text-5xl font-bold text-[#09090B]" data-testid="collo-seq">{collo.seq} / {collo.total}</div>
              <div className="text-[10px] text-zinc-400 uppercase tracking-widest font-bold mt-1">N° Collo</div>
              <div className={`mt-3 inline-block px-3 py-1 text-[11px] font-bold uppercase tracking-wide rounded-sm ${
                m.movement_type === "ingresso" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
              }`} data-testid="collo-type">
                {m.movement_type === "ingresso" ? "Ingresso merce" : "Uscita merce"}
              </div>
              {data.already_out && (
                <div className="mt-2 text-[11px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-1 inline-block" data-testid="collo-already-out">
                  Collo già uscito {data.already_out_at ? `il ${itDate(data.already_out_at)}` : ""}
                </div>
              )}
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="collo-info">
              <Row icon={Package} label="Articolo" value={collo.item_name} sub={collo.item_description} />
              <Row icon={User} label="Cliente" value={client.company || client.name || m.client_name || m.client_email}
                sub={[client.name, client.phone, client.city].filter(Boolean).join(" · ") || m.client_email} />
              <Row icon={Calendar} label="Data ingresso merce" value={itDate(m.entry_date)}
                sub={m.exit_date ? `Uscita: ${itDate(m.exit_date)}` : "Ancora in giacenza"} />
              <Row icon={Hash} label="Giorni giacenza" value={m.storage_days != null ? `${m.storage_days} gg` : "-"} />
              <Row icon={MapPin} label="Posizione magazzino" value={m.location_code || "-"} />
              <Row icon={Scale} label="Peso collo" value={collo.weight_kg ? `${collo.weight_kg} kg` : "-"} />
              <Row icon={Ruler} label="Misure (LxPxH cm)" value={collo.dimensions || "-"} />
              <Row icon={Package} label="Quantità articolo" value={`${collo.item_quantity || 1} ${collo.item_unit || "pz"}`}
                sub={data.stock_now != null ? `Giacenza attuale: ${data.stock_now}` : null} />
              <Row icon={FileText} label="Riferimento" value={m.reference || "-"} sub={m.description || null} />
              {m.note && <Row icon={FileText} label="Note" value={m.note} />}
            </div>

            {m.movement_type === "ingresso" && !data.already_out && (
              <button
                onClick={registerExit}
                disabled={exiting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] hover:bg-orange-600 text-white h-12 font-bold uppercase tracking-wide rounded-sm disabled:opacity-50"
                data-testid="collo-exit-btn"
              >
                {exiting ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpFromLine size={16} />}
                Registra uscita di questo collo
              </button>
            )}
            {data.already_out && (
              <div className="flex items-center justify-center gap-2 text-emerald-700 text-sm font-semibold">
                <CheckCircle2 size={16} /> Uscita già registrata
              </div>
            )}

            <Link to="/admin/magazzino" className="block text-center text-xs uppercase tracking-widest text-zinc-500 underline py-2" data-testid="collo-goto-warehouse">
              Vai al magazzino
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Icon size={16} className="text-zinc-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
        <div className="font-semibold text-[#09090B] break-words">{value || "-"}</div>
        {sub && <div className="text-[11px] text-zinc-500 break-words">{sub}</div>}
      </div>
    </div>
  );
}
